import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';

interface SudokuGame { puzzle: number[]; solution: number[]; }

type LevelId = 'easy' | 'medium' | 'hard';

const GSD_LEVELS: Record<LevelId, { label: string; clues: number }> = {
  easy: { label: 'Easy', clues: 40 },
  medium: { label: 'Medium', clues: 32 },
  hard: { label: 'Hard', clues: 26 },
};

function gsdShuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

function gsdOK(g: number[], i: number, v: number): boolean {
  const r = (i / 9) | 0, c = i % 9;
  for (let k = 0; k < 9; k++) { if (g[r * 9 + k] === v) return false; if (g[k * 9 + c] === v) return false; }
  const br = 3 * ((r / 3) | 0), bc = 3 * ((c / 3) | 0);
  for (let dr = 0; dr < 3; dr++) for (let dc = 0; dc < 3; dc++) if (g[(br + dr) * 9 + (bc + dc)] === v) return false;
  return true;
}

function gsdFill(g: number[]): boolean {
  const i = g.indexOf(0); if (i === -1) return true;
  for (const v of gsdShuffle([1, 2, 3, 4, 5, 6, 7, 8, 9])) { if (gsdOK(g, i, v)) { g[i] = v; if (gsdFill(g)) return true; g[i] = 0; } }
  return false;
}

function gsdCount(g: number[], limit: number): number {
  const i = g.indexOf(0); if (i === -1) return 1;
  let n = 0;
  for (let v = 1; v <= 9; v++) { if (gsdOK(g, i, v)) { g[i] = v; n += gsdCount(g, limit); g[i] = 0; if (n >= limit) break; } }
  return n;
}

function gsdGenerate(level: LevelId): SudokuGame {
  const sol = new Array(81).fill(0); gsdFill(sol);
  const puz = sol.slice(); const order = gsdShuffle([...Array(81).keys()]);
  let clues = 81; const target = GSD_LEVELS[level].clues;
  for (const i of order) { if (clues <= target) break; const bk = puz[i]; puz[i] = 0; if (gsdCount(puz.slice(), 2) !== 1) puz[i] = bk; else clues--; }
  return { puzzle: puz, solution: sol };
}

/**
 * Sudoku — backtracking generator with unique-solution digging, three difficulties
 * and keyboard entry (1–9 sets, Backspace/Delete/0 erases). Ported from the
 * OTA-B2B-6 design (games2.jsx). Default (Emulated) encapsulation keeps its `sd-*`
 * board CSS scoped — no clash with the Entertainment Sudoku.
 */
@Component({
  selector: 'app-mini-sudoku',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mini-sudoku.component.html',
  styleUrl: './mini-sudoku.component.scss',
})
export class MiniSudokuComponent implements OnInit, OnDestroy {
  readonly levels = (Object.entries(GSD_LEVELS) as [LevelId, { label: string; clues: number }][])
    .map(([id, v]) => ({ id, label: v.label }));

  readonly level = signal<LevelId>('easy');
  readonly game = signal<SudokuGame>(gsdGenerate('easy'));
  readonly cells = signal<number[]>(this.game().puzzle.slice());
  readonly sel = signal<number | null>(null);

  readonly solved = computed(() => { const g = this.game(); return this.cells().every((v, i) => v === g.solution[i]); });

  private readonly onKey = (e: KeyboardEvent): void => {
    if (this.sel() == null) return;
    if (/^[1-9]$/.test(e.key)) this.set(+e.key);
    else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') this.set(0);
  };

  ngOnInit(): void { window.addEventListener('keydown', this.onKey); }
  ngOnDestroy(): void { window.removeEventListener('keydown', this.onKey); }

  rc(i: number): [number, number] { return [Math.floor(i / 9), i % 9]; }

  isGiven(i: number): boolean { return this.game().puzzle[i] !== 0; }

  conflict(i: number): boolean {
    const cells = this.cells();
    const v = cells[i]; if (!v) return false;
    const [r, c] = this.rc(i);
    for (let k = 0; k < 81; k++) {
      if (k === i || cells[k] !== v) continue;
      const [r2, c2] = this.rc(k);
      if (r2 === r || c2 === c || (Math.floor(r2 / 3) === Math.floor(r / 3) && Math.floor(c2 / 3) === Math.floor(c / 3))) return true;
    }
    return false;
  }

  set(v: number): void {
    const sel = this.sel();
    if (sel == null || this.isGiven(sel)) return;
    this.cells.update((cs) => cs.map((x, i) => (i === sel ? (x === v ? 0 : v) : x)));
  }

  select(i: number): void { this.sel.set(i); }

  peer(i: number): boolean {
    const sel = this.sel(); if (sel == null) return false;
    const [r, c] = this.rc(i); const [sr, sc] = this.rc(sel);
    return r === sr || c === sc || (Math.floor(r / 3) === Math.floor(sr / 3) && Math.floor(c / 3) === Math.floor(sc / 3));
  }

  same(i: number): boolean {
    const sel = this.sel(); if (sel == null) return false;
    const cells = this.cells(); const selV = cells[sel];
    return !!selV && cells[i] === selV;
  }

  bb(i: number): boolean { const r = Math.floor(i / 9); return r % 3 === 2 && r < 8; }

  borderRight(i: number): string | null { const c = i % 9; return c % 3 === 2 && c < 8 ? '1.5px solid #3a4055' : null; }

  newGame(lv: LevelId = this.level()): void {
    const g = gsdGenerate(lv);
    this.level.set(lv);
    this.game.set(g);
    this.cells.set(g.puzzle.slice());
    this.sel.set(null);
  }
}
